import { ActionTypes } from './actions';
import { isAiAuthored } from '../constants';
import { v4 as uuidv4 } from 'uuid';

export const PANEL_MODES = {
    COLLAPSED: 'collapsed',
    WIDGET: 'widget',
    SIDEBAR: 'sidebar',
    FULLSCREEN: 'fullscreen',
};

export const MESSAGE_ROLES = {
    USER: 'user',
    ASSISTANT: 'assistant',
};

/*
 * Temporary key for a brand-new conversation whose server id isn't known yet;
 * swapped for the real id via ADOPT_CONVERSATION once the backend responds.
 */
export const PENDING_CONVERSATION_ID = '__pending__';

export const initialState = {
    conversations: [],
    /*
     * The list's own paging. It lives here rather than in the popover because the
     * provider is what replaces the list on a silent refresh — with the counter local
     * to the popover, that refresh left it asking for a page past the rows it holds.
     */
    conversationsPage: 1,
    conversationsHasNextPage: false,
    activeConversationId: null,
    activeConversation: null,
    messages: {},
    messagePagination: {},
    isStreaming: false,
    isLoadingConversations: false,
    isLoadingConversation: false,
    isLoadingEarlierMessages: false,
    error: null,
    panelMode: PANEL_MODES.COLLAPSED,
    context: null,
};

export const generateMessageId = () => uuidv4();
export const generateConversationId = () => uuidv4();

export default function aiAssistantReducer(state, action) {
    switch (action.type) {
        case ActionTypes.SET_PANEL_MODE:
            return { ...state, panelMode: action.payload };

        case ActionTypes.SET_CONTEXT:
            return { ...state, context: action.payload };

        case ActionTypes.SET_CONVERSATIONS: {
            const {
                mode,
                conversations: convList = [],
                hasNextPage = false,
            } = action.payload || {};
            // Mode-scoped replace: conversations tagged with another mode survive,
            // which is what keeps the untagged optimistic placeholders (e.g. from
            // ADOPT_CONVERSATION) on the list. When mode is omitted, replace the
            // whole list. Either way a placeholder whose id is already in the
            // incoming list is dropped rather than duplicated.
            const incomingIds = new Set(convList.map(c => c.id));
            const preserved = mode
                ? state.conversations.filter(c => c.mode !== mode && !incomingIds.has(c.id))
                : [];
            const merged = [...preserved, ...convList];
            const listEntry = state.activeConversationId
                ? merged.find(c => c.id === state.activeConversationId)
                : null;
            // Merge list entry onto the existing activeConversation so that a provisional
            // title set client-side isn't wiped out if the server hasn't generated one yet.
            const updatedActiveConversation = listEntry
                ? {
                      ...(state.activeConversation || {}),
                      ...listEntry,
                      title: listEntry.title || state.activeConversation?.title || null,
                  }
                : state.activeConversation;
            return {
                ...state,
                conversations: merged,
                conversationsPage: 1,
                conversationsHasNextPage: hasNextPage,
                activeConversation: updatedActiveConversation,
            };
        }

        case ActionTypes.APPEND_CONVERSATIONS: {
            const {
                conversations: convList = [],
                page,
                hasNextPage = false,
            } = action.payload || {};
            const existing = new Set(state.conversations.map(c => c.id));
            const deduped = convList.filter(c => !existing.has(c.id));
            return {
                ...state,
                conversations: [...state.conversations, ...deduped],
                conversationsPage: page || state.conversationsPage,
                conversationsHasNextPage: hasNextPage,
            };
        }

        case ActionTypes.ADD_CONVERSATION: {
            const conversation = action.payload;
            return {
                ...state,
                conversations: [conversation, ...state.conversations],
                activeConversationId: conversation.id,
                activeConversation: conversation,
                messages: {
                    ...state.messages,
                    [conversation.id]: [],
                },
            };
        }

        case ActionTypes.SET_ACTIVE_CONVERSATION:
            return {
                ...state,
                activeConversationId: action.payload,
                // Keep the invariant: activeConversation is null iff there's no active
                // id. When the id isn't in the list yet (restore/deep-link, before
                // loadConversation hydrates), fall back to a bare { id } — consumers
                // read it with optional chaining, so it behaves like the full object.
                activeConversation: action.payload
                    ? state.conversations.find(c => c.id === action.payload) || {
                          id: action.payload,
                      }
                    : null,
                error: null,
                isStreaming: false,
            };

        case ActionTypes.UPDATE_CONVERSATION: {
            const { conversationId, data } = action.payload;
            const isActive = state.activeConversationId === conversationId;
            return {
                ...state,
                conversations: state.conversations.map(c =>
                    c.id === conversationId ? { ...c, ...data } : c
                ),
                activeConversation: isActive
                    ? { ...(state.activeConversation || { id: conversationId }), ...data }
                    : state.activeConversation,
            };
        }

        case ActionTypes.ADOPT_CONVERSATION: {
            const { tempId, realId } = action.payload;
            /*
             * Adoption is a one-time move of the pending bucket, so it has to be
             * idempotent: a stream reports its conversation id on every event, and a
             * second adoption would move an already-empty bucket over the transcript
             * the real id now holds — blanking the thread back to a new chat while the
             * conversation itself lives on in the list.
             */
            const pendingMessages = state.messages[tempId];
            if (tempId === realId || !pendingMessages) return state;
            const newMessages = { ...state.messages, [realId]: pendingMessages };
            delete newMessages[tempId];
            // Add a placeholder so UPDATE_CONVERSATION can patch in the title once fetched.
            const placeholder = { id: realId };
            return {
                ...state,
                activeConversationId: realId,
                activeConversation: placeholder,
                conversations: [placeholder, ...state.conversations.filter(c => c.id !== tempId)],
                messages: newMessages,
            };
        }

        case ActionTypes.SET_MESSAGES: {
            const { conversationId, messages } = action.payload;
            const { [conversationId]: _removed, ...remainingPagination } = state.messagePagination;
            // Preserve citations accumulated during streaming. Local message IDs are UUIDs generated
            // client-side; the API reloads use server-assigned IDs, so we can't match by ID.
            // Instead, match by position: find every assistant message in existing state that has
            // citations, and transfer them to the corresponding assistant message in the new list
            // (same ordinal index among assistant messages only).
            // Only AI replies are counted: employee messages share the assistant role,
            // and counting them would shift every ordinal after the first agent reply
            // lands in an escalated thread.
            const isAiReply = m => m.role === MESSAGE_ROLES.ASSISTANT && isAiAuthored(m);
            const existingMessages = state.messages[conversationId] || [];
            const existingAssistantCitations = existingMessages
                .filter(m => isAiReply(m) && m.citations?.length)
                .map(m => m.citations);
            let assistantIdx = 0;
            const mergedMessages = messages.map(m => {
                if (isAiReply(m)) {
                    const citations = existingAssistantCitations[assistantIdx];
                    assistantIdx += 1;
                    return citations ? { ...m, citations } : m;
                }
                return m;
            });
            return {
                ...state,
                messages: {
                    ...state.messages,
                    [conversationId]: mergedMessages,
                },
                messagePagination: remainingPagination,
            };
        }

        case ActionTypes.ADD_MESSAGE: {
            const { conversationId, message } = action.payload;
            const existing = state.messages[conversationId] || [];
            // Bump conversation to top of list when a user message is sent
            const reorderedConversations =
                message.role === MESSAGE_ROLES.USER
                    ? [
                          ...state.conversations.filter(c => c.id === conversationId),
                          ...state.conversations.filter(c => c.id !== conversationId),
                      ]
                    : state.conversations;
            return {
                ...state,
                conversations: reorderedConversations,
                messages: {
                    ...state.messages,
                    [conversationId]: [...existing, message],
                },
            };
        }

        case ActionTypes.REMOVE_MESSAGE: {
            // Drops a client-side placeholder that will never be filled — a reply
            // placeholder on a conversation the Privacy Team owns, where chat-api
            // persists the message and runs no AI turn.
            const { conversationId, messageId } = action.payload;
            const msgs = state.messages[conversationId];
            if (!msgs) return state;
            return {
                ...state,
                messages: {
                    ...state.messages,
                    [conversationId]: msgs.filter(m => m.id !== messageId),
                },
            };
        }

        case ActionTypes.UPDATE_MESSAGE_ID: {
            const { conversationId, oldId, newId } = action.payload;
            const msgs = state.messages[conversationId] || [];
            return {
                ...state,
                messages: {
                    ...state.messages,
                    [conversationId]: msgs.map(m =>
                        m.id === oldId ? { ...m, id: newId, stableId: m.id } : m
                    ),
                },
            };
        }

        case ActionTypes.APPEND_TOKEN: {
            const { conversationId, token } = action.payload;
            const msgs = state.messages[conversationId] || [];
            if (msgs.length === 0) return state;
            const lastMsg = msgs[msgs.length - 1];
            if (lastMsg.role !== MESSAGE_ROLES.ASSISTANT) return state;
            return {
                ...state,
                messages: {
                    ...state.messages,
                    [conversationId]: [
                        ...msgs.slice(0, -1),
                        { ...lastMsg, content: lastMsg.content + token },
                    ],
                },
            };
        }

        case ActionTypes.PREPEND_MESSAGES: {
            const { conversationId, messages: newMessages } = action.payload;
            const existing = state.messages[conversationId] || [];
            const existingIds = new Set(existing.map(m => m.id));
            const deduplicated = newMessages.filter(m => !existingIds.has(m.id));
            return {
                ...state,
                messages: {
                    ...state.messages,
                    [conversationId]: [...deduplicated, ...existing],
                },
            };
        }

        case ActionTypes.SET_MESSAGE_PAGINATION: {
            const { conversationId, nextCursor, hasNext } = action.payload;
            return {
                ...state,
                messagePagination: {
                    ...state.messagePagination,
                    [conversationId]: { nextCursor: nextCursor || null, hasNext: hasNext || false },
                },
            };
        }

        case ActionTypes.SET_MESSAGE_CITATIONS: {
            const { conversationId, messageId, citations } = action.payload;
            const msgs = state.messages[conversationId] || [];
            return {
                ...state,
                messages: {
                    ...state.messages,
                    [conversationId]: msgs.map(m =>
                        m.id === messageId
                            ? { ...m, citations: [...(m.citations || []), ...citations] }
                            : m
                    ),
                },
            };
        }

        case ActionTypes.SET_STREAMING:
            return { ...state, isStreaming: action.payload };

        case ActionTypes.SET_LOADING_EARLIER_MESSAGES:
            return { ...state, isLoadingEarlierMessages: action.payload };

        case ActionTypes.SET_LOADING_CONVERSATIONS:
            return { ...state, isLoadingConversations: action.payload };

        case ActionTypes.SET_LOADING_CONVERSATION:
            return { ...state, isLoadingConversation: action.payload };

        case ActionTypes.SET_ERROR:
            return { ...state, error: action.payload, isStreaming: false };

        case ActionTypes.CLEAR_ERROR:
            return { ...state, error: null };

        case ActionTypes.REMOVE_CONVERSATION: {
            const { conversationId } = action.payload;
            const { [conversationId]: _msgs, ...remainingMessages } = state.messages;
            const { [conversationId]: _pagination, ...remainingPagination } =
                state.messagePagination;
            const isActive = state.activeConversationId === conversationId;
            return {
                ...state,
                conversations: state.conversations.filter(c => c.id !== conversationId),
                messages: remainingMessages,
                messagePagination: remainingPagination,
                activeConversationId: isActive ? null : state.activeConversationId,
                activeConversation: isActive ? null : state.activeConversation,
            };
        }

        case ActionTypes.RESET:
            return initialState;

        default:
            return state;
    }
}
